import { ICommandBarItemProps, ContextualMenuItemType } from '@fluentui/react';
import { useAnalytics } from '@msrceng/analytics';
import { useColumnSelect } from '@msrceng/component';
import { compact } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useDownloadMenu } from '../download/useDownloadMenu';
import { useDateSelector } from '../SearchFilter/field/date';
import { useFilterData } from '../SearchFilter/filterContext';
import { useSearchListData } from '../SearchListContext';

/**
 * Logic to get the near items for the table command bar.
 */
export const useTableCommandBarNearItems = () => {
  const [t] = useTranslation('search');
  const { toggle: toggleColumnSelect } = useColumnSelect();
  const { filterSettings, groupByOptionList } = useFilterData();
  const { isFilterVisible, setIsFilterVisible, tableName, hideDatePicker } =
    useSearchListData();
  const downloadMenu = useDownloadMenu();
  const { appInsights } = useAnalytics();
  const { startDate, endDate, setStartDate, setEndDate } =
    useFilterData().commonFilterState;

  const dateSelector = useDateSelector({
    startDateExternal: startDate,
    endDateExternal: endDate,
    setStartDateExternal: setStartDate,
    setEndDateExternal: setEndDate,
  });

  const showFilterToggle =
    (filterSettings &&
      // Don't show the filter toggle if there are no filters (not counting
      // the 'always' filter type)
      filterSettings.filter(f => f.type !== 'always').length > 0) ||
    (groupByOptionList && groupByOptionList?.length > 0);

  const nearItemBase: ICommandBarItemProps[] = compact([
    !hideDatePicker && dateSelector,
    hideDatePicker && {
      key: 'dummy',
      itemType: ContextualMenuItemType.Divider,
      onRender: () => <div role='presentation' />,
    },
    // TODO: move this back to far items whenever they fix the overflow issue
    // https://github.com/microsoft/fluentui/issues/11842
    {
      key: 'spacer',
      itemType: ContextualMenuItemType.Divider,
      onRender: () => <div role='presentation' />,
    },
    {
      key: 'columnSelect',
      iconProps: {
        iconName: 'Edit',
      },
      text: t('Edit Columns', { ns: 'search', defaultValue: 'Edit columns' }),
      onClick: () => toggleColumnSelect(),
    },
    downloadMenu,
    showFilterToggle
      ? {
          key: 'filterToggle',
          checked: isFilterVisible,
          onClick: () => {
            appInsights?.trackEvent?.({
              name: 'Filter toggle clicked',
              properties: {
                table: tableName,
              },
            });
            setIsFilterVisible && setIsFilterVisible(v => !v);
          },
          ariaLabel: isFilterVisible
            ? t('FilterExpanded')
            : t('FilterCollapsed'),
          iconProps: { iconName: 'Filter' },
          title: t('Filters'),
          text: t('Filters'),
        }
      : undefined,
  ]);

  return nearItemBase.filter(w => !!w);
};
